import * as React from 'react'

import {
  Box,
  type Colors,
  Icon,
  type MediumIcon,
  Tooltip,
  useToggle,
} from '@factorialco/deprecated-design-system'

import { useAsyncData } from 'modules/core/hooks/useAsyncData'
import { stringToDate } from 'modules/core/lib/dates'
import i18n from 'modules/core/lib/i18n'

import SupplementSyncStore from 'modules/payroll_integrations_kombo/lib/SupplementSync/store'
import type { IntegrationTools } from 'modules/payroll_integrations_kombo/lib/constants'
import type { Status } from 'modules/payroll_integrations_kombo/store/models/SupplementSync'
import type SupplementSync from 'modules/payroll_integrations_kombo/store/models/SupplementSync'

const colorByStatus: Record<Status, Colors> = {
  pending: 'grey600',
  synced: 'viridian1000',
  failed: 'radicalRed1200',
}

const iconByStatus: Record<Status, MediumIcon> = {
  pending: 'link-remove',
  synced: 'link',
  failed: 'link',
}

interface Props {
  supplementId: string
  policyPeriodId: string
  policyPeriodStatus: string
  integrationTool: IntegrationTools
  integrationLabel: string
  hover: boolean
}

const TooltipTrigger = ({ status }: { status: Status }) => (
  <Box height="full" justifyContent="center">
    <Icon.Medium icon={iconByStatus[status]} color={colorByStatus[status]} />
  </Box>
)

const SupplementSyncStatus = ({
  supplementId,
  policyPeriodId,
  policyPeriodStatus,
  integrationTool,
  integrationLabel,
  hover,
}: Props) => {
  const [sync, setSync] = React.useState<SupplementSync>()
  const [open, toggle] = useToggle(false)

  useAsyncData(async () => {
    const sync = await SupplementSyncStore.fetch(
      supplementId,
      policyPeriodId,
      policyPeriodStatus,
      integrationTool
    )
    setSync(sync)
  }, [])

  const tooltipLabel = (): string => (sync!.isSynced ? sycnedLabel() : pendingLabel())

  const pendingLabel = (): string =>
    i18n.t('payroll_integrations.kombo.supplements_sync.status.pending', {
      integrationLabel: integrationLabel,
    })

  const sycnedLabel = (): string =>
    i18n.t('payroll_integrations.kombo.supplements_sync.status.synced', {
      sentAt: stringToDate(sync!.sent_at, 'backend_datetime').format('MMM Do HH:mm:ss'),
      integrationLabel: integrationLabel,
    })

  if (!sync) return null

  if (!hover) return <TooltipTrigger status={sync.status} />

  return (
    <Tooltip.Alt
      label={tooltipLabel()}
      toggle={toggle}
      attachment="bottom center"
      open={open}
      size="small"
    >
      <TooltipTrigger status={sync.status} />
    </Tooltip.Alt>
  )
}

export default SupplementSyncStatus
